"use client";

import { cn } from "@/utils/cn";
import { useEffect, useRef, useState } from "react";
import { Checkbox as AriaCheckbox, Group, Label, Text } from "react-aria-components";

type Option = { label: string; value: string };

export interface CheckboxGroupInputProps {
	label?: string;
	description?: string;
	errorMessage?: string;
	options?: Option[];
	value?: string[];
	onChange: (value: string[]) => void;
	className?: string;
	checkboxClassName?: string;
	children?: React.ReactNode;
	itemsClassName?: string;
}

export const CheckboxGroupInput = ({
	label,
	description,
	errorMessage,
	options,
	value: initialValue,
	onChange,
	className,
	checkboxClassName,
	children,
	itemsClassName = "grid grid-cols-2 gap-2",
}: CheckboxGroupInputProps) => {
	const [selectedValue, setSelectedValue] = useState<string[]>(initialValue || []);
	const initialValueRef = useRef<string[]>(initialValue || []);

	useEffect(() => {
		// Only update if the initial value has actually changed
		const newValue = initialValue || [];
		if (JSON.stringify(initialValueRef.current) !== JSON.stringify(newValue)) {
			setSelectedValue(newValue);
			initialValueRef.current = newValue;
		}
	}, [initialValue]);

	const handleChange = (selected: boolean, value: string) => {
		const updatedValue = selected ? [...selectedValue, value] : selectedValue.filter(sv => sv !== value);
		setSelectedValue(updatedValue);
		onChange(updatedValue);
	};

	return (
		<Group className={cn("group flex flex-col py-3 my-3 gap-2", className)}>
			{label && <Label className="text-title-medium">{label}</Label>}

			<div className={cn(itemsClassName)}>
				{options
					? options.map(option => {
							const curValue = option.value;
							const isSelected = selectedValue.includes(option.value);
							return (
								<AriaCheckbox
									key={curValue}
									isSelected={isSelected}
									onChange={selected => handleChange(selected, curValue)}
									className={cn(
										"flex items-center gap-2 px-4 py-2 rounded-xl border transition-all cursor-pointer text-body w-full",
										"border-primary-600 text-primary-600 relative z-10",
										isSelected ? "bg-primary-400" : "bg-white hover:bg-gray-50",
										"focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
										"pointer-events-auto touch-manipulation",
										checkboxClassName,
									)}
								>
									<div
										className={cn(
											"w-4 h-4 border flex items-center justify-center rounded flex-shrink-0",
											isSelected ? "border-primary-600 bg-primary-600" : "border-primary-600 bg-white",
										)}
									>
										{isSelected && <div className="w-2 h-2 bg-white rounded-sm flex-shrink-0" />}
									</div>
									<span>{option.label}</span>
								</AriaCheckbox>
							);
						})
					: children}
			</div>

			{description && (
				<Text slot="description" className="text-body-small">
					{description}
				</Text>
			)}

			<Text slot="errorMessage" className="text-body-small-medium text-destructive h-1">
				{errorMessage || ""}
			</Text>
		</Group>
	);
};
